import { useState } from "react";
import { CheckCircle, Loader2 } from "lucide-react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

interface ShareEmailDialogProps {
	open: boolean;
	title: string;
	description: string;
	ariaLabel: string;
	testIdPrefix: string;
	onSend: (message: string | undefined) => Promise<boolean>;
	onClose: () => void;
}

export const ShareEmailDialog: React.FC<ShareEmailDialogProps> = ({
	open,
	title,
	description,
	ariaLabel,
	testIdPrefix,
	onSend,
	onClose,
}) => {
	const [message, setMessage] = useState("");
	const [isSending, setIsSending] = useState(false);

	const handleClose = () => {
		setMessage("");
		onClose();
	};

	const handleSend = async () => {
		setIsSending(true);
		const sent = await onSend(message.trim() || undefined);
		setIsSending(false);
		if (sent) handleClose();
	};

	return (
		<Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
			<DialogContent
				className="sm:max-w-md"
				aria-label={ariaLabel}
				data-testid={`${testIdPrefix}-dialog`}
			>
				<DialogHeader>
					<div className="flex items-center gap-2">
						<CheckCircle className="h-5 w-5 text-green-600" />
						<DialogTitle>{title}</DialogTitle>
					</div>
					<DialogDescription>{description}</DialogDescription>
				</DialogHeader>

				<Textarea
					placeholder="Optionale Nachricht hinzufügen..."
					value={message}
					onChange={(event) => setMessage(event.target.value)}
					disabled={isSending}
					className="min-h-[100px]"
					aria-label="Optionale Nachricht"
					data-testid={`${testIdPrefix}-textarea`}
				/>

				<DialogFooter className="flex-row gap-2 sm:justify-end">
					<Button
						variant="outline"
						onClick={handleClose}
						disabled={isSending}
						aria-label="E-Mail überspringen"
						data-testid={`${testIdPrefix}-cancel-button`}
					>
						Überspringen
					</Button>
					<Button
						onClick={handleSend}
						disabled={isSending}
						aria-label="E-Mail senden"
						data-testid={`${testIdPrefix}-send-button`}
					>
						{isSending ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Senden...
							</>
						) : (
							"E-Mail senden"
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
